import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Preencha nome, telefone e mensagem.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      if (data.whatsapp_sent) {
        toast.success("Mensagem enviada! Verifique seu WhatsApp — já respondemos.");
      } else {
        toast.success("Recebemos sua mensagem! Continue no WhatsApp para o pré-atendimento.");
        if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
      }
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Não conseguimos enviar agora. Tente novamente ou fale direto no WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50">
        <div className="container-cuidar py-14">
          <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--secondary))]">Contato</div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black leading-tight">Fale com o Instituto Cuidar</h1>
          <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Envie sua mensagem e nossa equipe entrará em contato via WhatsApp para o pré-atendimento humano.
          </p>
        </div>
      </section>

      <section className="container-cuidar py-14 grid gap-10 lg:grid-cols-[1.15fr_1fr]">
        <form onSubmit={submit} className="surface p-7 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name">Nome *</Label>
              <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 h-12 rounded-xl" data-testid="contact-name-input" />
            </div>
            <div>
              <Label htmlFor="c-phone">WhatsApp *</Label>
              <Input id="c-phone" placeholder="(11) 99999-9999" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1.5 h-12 rounded-xl" data-testid="contact-phone-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-email">E-mail (opcional)</Label>
            <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 h-12 rounded-xl" data-testid="contact-email-input" />
          </div>
          <div>
            <Label htmlFor="c-message">Como podemos ajudar? *</Label>
            <Textarea id="c-message" required rows={5}
              placeholder="Conte um pouco sobre sua família e sua dúvida…"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1.5 rounded-xl" data-testid="contact-message-input" />
          </div>
          <Button type="submit" disabled={loading} data-testid="contact-submit-btn"
            className="btn-pill h-12 px-8 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
            {loading ? "Enviando…" : "Enviar mensagem"}
          </Button>
        </form>

        <aside className="space-y-4">
          <div className="surface p-6">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#25D366]/15 text-[#25D366]"><MessageCircle className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading text-lg font-extrabold">Pré-atendimento pelo WhatsApp</h3>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              Preferimos conversar por WhatsApp — é mais ágil e mais próximo. Ao enviar o formulário, iniciamos por lá.
            </p>
          </div>
          <div className="surface p-6">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"><Phone className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading text-lg font-extrabold">Telefone</h3>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="contact-phone">
              <a href="tel:+5571999687054" className="hover:text-[hsl(var(--primary))]">+55 (71) 99968-7054</a>
            </p>
          </div>
          <div className="surface p-6">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))]"><Mail className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading text-lg font-extrabold">E-mail</h3>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="contact-email">
              <a href="mailto:suporteinstitutocuidar@gmail.com" className="hover:text-[hsl(var(--primary))] break-all">suporteinstitutocuidar@gmail.com</a>
            </p>
          </div>
          <div className="surface p-6">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]"><MapPin className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading text-lg font-extrabold">Endereço</h3>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="contact-address">
              Avenida Estados Unidos, 397<br />
              Salvador — Bahia
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
